import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import { callApi, CDN_URL, formatDate, getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import { MdArrowBack, MdDelete, MdEdit, MdForum, MdSend } from "react-icons/md";
import { Editor } from "@tinymce/tinymce-react";

function stripHtml(text = "") {
  return String(text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function hasMeaningfulContent(text = "") {
  return stripHtml(text).length > 0;
}

export default function ForumThreadView({ uuid }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [threadData, setThreadData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingThread, setEditingThread] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const editEditorRef = useRef(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageBody, setEditMessageBody] = useState("");

  const canPost =
    appContext.auth &&
    String(appContext.currentUserData?.status || "")
      .trim()
      .toLowerCase() === "registered";

  async function loadThread() {
    setReady(false);
    const [thread, threadMessages] = await Promise.all([
      appContext.getThreadData(uuid),
      appContext.getThreadMessages(uuid),
    ]);

    setThreadData(thread);
    setMessages(Array.isArray(threadMessages) ? threadMessages : []);
    setEditTitle(thread?.title || "");
    setEditBody(thread?.body || "");
    setReady(true);
  }

  async function imageUploadHandler(blobInfo) {
    return new Promise(async (resolve, reject) => {
      await callApi("upload-attachment", { file: blobInfo.blob() }).then(
        (response) => {
          if (response.status === 1 && response.files?.file) {
            resolve(CDN_URL + response.files.file);
          } else {
            appContext.tellError("Could not upload attachment");
            reject("Could not upload attachment");
          }
        },
      );
    });
  }

  async function filePickerCallback(callback, value, meta) {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute(
      "accept",
      meta.filetype === "image" ? "image/*" : "video/*",
    );

    input.onchange = async function () {
      const file = this.files[0];

      await callApi("upload-attachment", { file }).then((response) => {
        if (response.status === 1 && response.files?.file) {
          callback(CDN_URL + response.files.file);
        } else {
          appContext.tellError("Could not upload attachment");
        }
      });
    };

    input.click();
  }

  async function handleUpdateThread() {
    if (!editTitle.trim() || !hasMeaningfulContent(editBody)) {
      appContext.tellWarning("Please fill in both title and body.");
      return;
    }

    const response = await appContext.updateThread({
      uuid,
      title: editTitle.trim(),
      body: editBody.trim(),
    });

    if (response.status === 1) {
      appContext.tellMessage("Thread updated successfully.");
      setEditingThread(false);
      await loadThread();
    } else {
      appContext.tellError(response.msg || "Could not update the thread.");
    }
  }

  async function handleDeleteThread() {
    if (!window.confirm("Delete this thread and hide it from the forum?")) {
      return;
    }

    const response = await appContext.deleteThread({ uuid });

    if (response.status === 1) {
      appContext.tellMessage("Thread deleted successfully.");
      appContext.navTo({ item: "forum" });
    } else {
      appContext.tellError(response.msg || "Could not delete the thread.");
    }
  }

  async function handleReplySubmit(event) {
    event.preventDefault();

    if (!canPost || submitting) {
      return;
    }

    if (!reply.trim()) {
      appContext.tellWarning("Please enter your reply.");
      return;
    }

    setSubmitting(true);
    const response = await appContext.createThreadMessage({
      threadUuid: uuid,
      body: reply.trim(),
    });
    setSubmitting(false);

    if (response.status === 1) {
      setReply("");
      appContext.tellMessage("Reply posted successfully.");
      await loadThread();
    } else {
      appContext.tellError(response.msg || "Could not post your reply.");
    }
  }

  function startMessageEdit(message) {
    setEditingMessage(message.uuid);
    setEditMessageBody(message.body || "");
  }

  function cancelMessageEdit() {
    setEditingMessage(null);
    setEditMessageBody("");
  }

  async function handleUpdateMessage(messageUuid) {
    if (!editMessageBody.trim()) {
      appContext.tellWarning("Reply body cannot be empty.");
      return;
    }

    const response = await appContext.updateThreadMessage({
      uuid: messageUuid,
      body: editMessageBody.trim(),
    });

    if (response.status === 1) {
      appContext.tellMessage("Reply updated successfully.");
      cancelMessageEdit();
      await loadThread();
    } else {
      appContext.tellError(response.msg || "Could not update the reply.");
    }
  }

  async function handleDeleteMessage(messageUuid) {
    if (!window.confirm("Delete this reply?")) {
      return;
    }

    const response = await appContext.deleteThreadMessage({
      uuid: messageUuid,
    });

    if (response.status === 1) {
      appContext.tellMessage("Reply deleted successfully.");
      await loadThread();
    } else {
      appContext.tellError(response.msg || "Could not delete the reply.");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadThread();
  }, [uuid]);

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  if (!threadData) {
    return (
      <div className="MainBodySection py-5 bg-background">
        <div className="container">
          <div className="newsletter-modal-card membership-panel-card text-center">
            <h4 className="font-bold text-primary">Thread not found</h4>
            <p className="text-muted mb-4">
              The discussion may have been deleted or is no longer available.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => appContext.navTo({ item: "forum" })}
            >
              Back to forum
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isOwner = threadData.userUuid === appContext.currentUserData?.uuid;

  return (
    <>
      <div className="MainBodySection py-5 bg-background">
        <div className="container">
          <div className="d-flex flex-wrap gap-2 align-items-center mb-4">
            <button
              className="btn btn-outline-primary"
              onClick={() => appContext.navTo({ item: "forum" })}
            >
              <MdArrowBack className="me-2" />
              Back to forum
            </button>
          </div>

          <div
            className="card border-0 shadow-sm mb-4"
            style={{ borderRadius: "20px" }}
          >
            <div className="card-body p-4 p-lg-5">
              {editingThread ? (
                <div>
                  <div className="mb-3">
                    <label className="mb-2">Thread title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editTitle}
                      onChange={(event) => setEditTitle(event.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="mb-2">Thread body</label>
                    <Editor
                      key={`thread-editor-${uuid}`}
                      tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js"
                      initialValue={editBody || "<p></p>"}
                      ref={editEditorRef}
                      init={{
                        height: 320,
                        menubar: false,
                        branding: false,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "preview",
                          "anchor",
                          "searchreplace",
                          "visualblocks",
                          "code",
                          "fullscreen",
                          "insertdatetime",
                          "media",
                          "table",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | formatselect | bold italic backcolor | " +
                          "alignleft aligncenter alignright alignjustify | " +
                          "bullist numlist outdent indent | removeformat | image media | help",
                        images_upload_handler: imageUploadHandler,
                        file_picker_callback: filePickerCallback,
                        image_title: true,
                        automatic_uploads: true,
                      }}
                      onEditorChange={(content) => setEditBody(content)}
                    />
                  </div>

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateThread}
                    >
                      Save changes
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setEditingThread(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
                    <div>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge bg-light text-primary border">
                          <MdForum className="me-1" />
                          Discussion
                        </span>
                        {isOwner ? (
                          <span className="badge bg-secondary">
                            Your thread
                          </span>
                        ) : null}
                      </div>

                      <h2 className="font-bold text-primary mb-2">
                        {threadData.title}
                      </h2>
                      <p className="text-muted mb-0 d-flex flex-wrap gap-3">
                        <span>
                          <strong>By:</strong>{" "}
                          {threadData.fullName || "TEF Member"}
                        </span>
                        <span>
                          <strong>Posted:</strong>{" "}
                          {formatDate(threadData.dateCreated)}
                        </span>
                      </p>
                    </div>

                    {isOwner && canPost ? (
                      <div className="d-flex gap-2 flex-wrap align-items-start">
                        <button
                          className="btn btn-light border"
                          onClick={() => setEditingThread(true)}
                        >
                          <MdEdit className="me-1" />
                          Edit
                        </button>
                        <button
                          className="btn btn-light border text-danger"
                          onClick={handleDeleteThread}
                        >
                          <MdDelete className="me-1" />
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="p-4 rounded-4"
                    style={{
                      background: "rgba(18,60,140,0.04)",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: threadData.body || "<p>No content provided.</p>",
                    }}
                  />
                </>
              )}
            </div>
          </div>

          <div className="row g-4 align-items-stretch">
            <div className="col-lg-7 d-flex flex-column">
              {messages.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="font-bold text-primary mb-1">Replies</h4>
                      <p className="text-muted mb-0">
                        {messages.length} contribution(s) in this discussion.
                      </p>
                    </div>
                  </div>

                  {messages.map((message, index) => {
                  const isMessageOwner =
                    message.userUuid === appContext.currentUserData?.uuid;

                  return (
                    <div
                      key={message.uuid || index}
                      className="card border-0 shadow-sm mb-3"
                      style={{ borderRadius: "18px" }}
                    >
                      <div className="card-body p-4">
                        {editingMessage === message.uuid ? (
                          <div>
                            <textarea
                              className="form-control"
                              rows={4}
                              value={editMessageBody}
                              onChange={(event) =>
                                setEditMessageBody(event.target.value)
                              }
                            />
                            <div className="d-flex gap-2 flex-wrap mt-3">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() =>
                                  handleUpdateMessage(message.uuid)
                                }
                              >
                                Save reply
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={cancelMessageEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-2">
                              <div>
                                <h6 className="font-bold text-primary mb-1">
                                  {message.fullName || "TEF Member"}
                                </h6>
                                <p className="text-muted small mb-0">
                                  {formatDate(message.dateCreated)}
                                </p>
                              </div>

                              {isMessageOwner && canPost ? (
                                <div className="d-flex gap-2 flex-wrap">
                                  <button
                                    className="btn btn-light btn-sm border"
                                    onClick={() => startMessageEdit(message)}
                                  >
                                    <MdEdit className="me-1" />
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-light btn-sm border text-danger"
                                    onClick={() =>
                                      handleDeleteMessage(message.uuid)
                                    }
                                  >
                                    <MdDelete className="me-1" />
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </div>

                            <div style={{ whiteSpace: "pre-line" }}>
                              {message.body}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </>
              ) : (
                <div
                  className="newsletter-modal-card membership-panel-card text-center d-flex flex-column justify-content-center align-items-center flex-grow-1"
                  style={{ minHeight: "260px" }}
                >
                  <span className="newsletter-modal-badge mb-3">Replies</span>
                  <h5 className="font-bold text-primary mb-2">No replies yet</h5>
                  <p className="text-muted mb-0" style={{ maxWidth: "360px" }}>
                    Start the conversation by adding the first reply.
                  </p>
                </div>
              )}
            </div>

            <div className="col-lg-5 d-flex">
              {canPost ? (
                <div className="newsletter-modal-card membership-panel-card">
                  <span className="newsletter-modal-badge">
                    Reply to thread
                  </span>
                  <h5 className="font-bold text-primary mt-3 mb-2">
                    Add your contribution
                  </h5>
                  <p className="text-muted mb-4">
                    Keep replies respectful, concise, and helpful for other
                    members.
                  </p>

                  <form onSubmit={handleReplySubmit}>
                    <textarea
                      className="form-control newsletter-modal-input"
                      rows={6}
                      value={reply}
                      onChange={(event) => setReply(event.target.value)}
                      placeholder="Write your reply here..."
                      required
                    />

                    <button
                      type="submit"
                      className="btn btn-primary btn-padded mt-3"
                      disabled={submitting}
                    >
                      <MdSend className="me-2" />
                      {submitting ? "Posting..." : "Post reply"}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="newsletter-modal-card membership-panel-card">
                  <span className="newsletter-modal-badge">Read-only mode</span>
                  <h5 className="font-bold text-primary mt-3 mb-2">
                    Posting is restricted
                  </h5>
                  <p className="text-muted mb-0">
                    Only members with <strong>registered</strong> status can
                    reply in the forum.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
